import type { ReactNode } from 'react';
import classNames from 'classnames';

import { Caption } from '@/components/atoms/Eyebrow';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import Tooltip from '@/components/atoms/Tooltip';

import styles from './MetricSummaryRow.module.scss';

const SKELETON_WIDTH = 100;
const SKELETON_HEIGHT = 24;
const SKELETON_RADIUS = 8;

export interface MetricSummaryRowProps {
    /** Already translated. Rendered uppercase above the value. */
    label: string;
    value: string;
    isLoading?: boolean;
    /** Rendered next to the value — e.g. a trend arrow. */
    trend?: ReactNode;
    /**
     * Rendered at the far end of the row — e.g. an arrow through to the metric's page. It sits
     * outside the tooltip target, so reaching for it does not open the explanation.
     */
    action?: ReactNode;
    /**
     * Explains the metric on hover and focus. The target fits the label and value: spanning
     * the whole row, the bubble would point at its empty middle.
     */
    tooltip?: ReactNode;
    /** Keeps the tooltip shut, e.g. while the value it explains is still loading. */
    tooltipDisabled?: boolean;
    /** Class on the tooltip's target, the element wrapping the label and value. */
    tooltipTargetClassName?: string;
    /**
     * Applied to the row's outer element. The row carries its own bottom margin, which suits a
     * loose stack of rows.
     */
    className?: string;
    /** Drops the bottom margin, for a row whose container spaces it or divides it from the next. */
    flush?: boolean;
}

type MetricProps = Omit<
    MetricSummaryRowProps,
    'action' | 'tooltip' | 'tooltipDisabled' | 'tooltipTargetClassName'
>;

function Metric(props: MetricProps) {
    const { label, value, isLoading = false, trend, className } = props;

    return (
        <div className={classNames(styles.row, className)}>
            <Caption>{label}</Caption>
            {isLoading ? (
                <SkeletonBox
                    width={SKELETON_WIDTH}
                    height={SKELETON_HEIGHT}
                    borderRadius={SKELETON_RADIUS}
                />
            ) : (
                <div className={styles.valueRow}>
                    <span className={styles.value}>{value}</span>
                    {trend}
                </div>
            )}
        </div>
    );
}

function WithAction(props: { action: ReactNode; className?: string; children: ReactNode }) {
    const { action, className, children } = props;

    return (
        <div className={classNames(styles.withAction, className)}>
            {children}
            <div className={styles.action}>{action}</div>
        </div>
    );
}

/**
 * One label-over-value line in an information panel.
 *
 * No boxed chrome of its own, and a skeleton while the value loads. Pass `tooltip` to explain
 * the metric on hover and focus.
 */
export default function MetricSummaryRow(props: MetricSummaryRowProps) {
    const {
        action,
        className: callerClassName,
        flush = false,
        tooltip,
        tooltipDisabled,
        tooltipTargetClassName,
        ...metric
    } = props;
    const className = classNames(callerClassName, { [styles.flush]: flush });

    const explained = (rowClassName?: string) => (
        <Tooltip
            content={tooltip}
            disabled={tooltipDisabled}
            layout="fit"
            targetClassName={tooltipTargetClassName}>
            <Metric {...metric} className={rowClassName} />
        </Tooltip>
    );

    if (!action) {
        return explained(className);
    }

    // The action sits outside the tooltip target, so reaching for it does not open the
    // explanation.
    return (
        <WithAction action={action} className={className}>
            {explained()}
        </WithAction>
    );
}
